// Site-wide internal linking map (SEO / E-E-A-T).
// Keyed by page slug (calculator slug, blog slug, or program slug).
// Consumed by <RelatedResources slug="..." />.

// --- Canonical link atoms (reused across the map) ---
const P = {
  fertility: { label: 'Fertility Nutrition Program', to: '/fertility-nutrition' },
  pregnancy: { label: 'Pregnancy Nutrition Plan', to: '/pregnancy-nutrition' },
  postpartum: { label: 'Postpartum Recovery Program', to: '/postpartum-recovery' },
  mnt: { label: 'Clinical Nutrition (MNT)', to: '/medical-nutrition-therapy' },
  fitness: { label: 'Online Fitness Coaching', to: '/online-fitness-coaching' },
  doctorled: { label: 'Doctor-Led Fitness', to: '/doctor-led-fitness' },
  pmos: { label: 'PMOS Support & Diet', to: '/pcos-diet-plan' },
};

const G = {
  pcos: { label: 'PMOS Diet Plan: Complete Guide', to: '/blog/pcos-diet-plan-complete-guide' },
  fertility: { label: 'Fertility Nutrition: Foods for Hormone Balance', to: '/blog/fertility-nutrition-hormone-balance' },
  pregnancy: { label: 'Best Diet Plan for Pregnancy', to: '/blog/best-diet-plan-for-pregnancy' },
  postnatal: { label: 'Best Diet Plan After Delivery', to: '/blog/best-diet-plan-after-delivery' },
  oil: { label: 'Best Cooking Oil for Indian Cooking', to: '/blog/best-cooking-oil-indian-cooking-guide' },
  proteinPowder: { label: 'Types of Protein Powder Compared', to: '/blog/types-of-protein-powder-comparison' },
  proteinCompare: { label: 'Prawns vs Fish vs Chicken Protein', to: '/blog/prawns-fish-chicken-protein-comparison' },
  sports: { label: 'Weekend Sports Nutrition & Warm-Up', to: '/blog/weekend-sports-nutrition-warmup-guide' },
};

const T = {
  bmi: { label: 'BMI Calculator', to: '/calculators/bmi' },
  bodyFat: { label: 'Body Fat %', to: '/calculators/body-fat' },
  lbm: { label: 'Lean Body Mass', to: '/calculators/lean-body-mass' },
  idealWeight: { label: 'Ideal Weight', to: '/calculators/ideal-weight' },
  whr: { label: 'Waist-to-Hip Ratio', to: '/calculators/waist-to-hip' },
  ffmi: { label: 'FFMI', to: '/calculators/ffmi' },
  bsa: { label: 'Body Surface Area', to: '/calculators/bsa' },
  bmiPrime: { label: 'BMI Prime', to: '/calculators/bmi-prime' },
  tdee: { label: 'TDEE Calculator', to: '/calculators/tdee' },
  deficit: { label: 'Calorie Deficit', to: '/calculators/calorie-deficit' },
  macro: { label: 'Macro Calculator', to: '/calculators/macro' },
  protein: { label: 'Protein Intake', to: '/calculators/protein-intake' },
  water: { label: 'Water Intake', to: '/calculators/water-intake' },
  fasting: { label: 'Intermittent Fasting', to: '/calculators/intermittent-fasting' },
  oneRepMax: { label: 'One Rep Max', to: '/calculators/one-rep-max' },
  hrZones: { label: 'Heart Rate Zones', to: '/calculators/heart-rate-zones' },
  vo2: { label: 'VO₂ Max', to: '/calculators/vo2-max' },
  pace: { label: 'Pace Calculator', to: '/calculators/pace' },
  calorieBurn: { label: 'Calorie Burn', to: '/calculators/calorie-burn' },
  steps: { label: 'Steps to Calories', to: '/calculators/steps-to-calories' },
  pregnancyWeight: { label: 'Pregnancy Weight Gain', to: '/calculators/pregnancy-weight-gain' },
  sleep: { label: 'Sleep Calculator', to: '/calculators/sleep' },
  health: { label: 'Full Body Health Analysis', to: '/health-analysis' },
};

// --- Related-links map ---
// Each value: { programs?, guides?, tools? } with 2-3 links per group.
export const RELATED_LINKS = {
  // ===================== SERVICE / PROGRAM PAGES =====================
  'doctor-led-fitness': {
    guides: [G.oil, G.proteinPowder, G.sports],
    tools: [T.tdee, T.deficit, T.macro, T.health],
  },
  'fertility-nutrition': {
    programs: [P.pmos, P.pregnancy],
    guides: [G.fertility, G.pcos],
    tools: [T.whr, T.bmi],
  },
  'pregnancy-nutrition': {
    programs: [P.postpartum, P.mnt],
    guides: [G.pregnancy, G.postnatal],
    tools: [T.pregnancyWeight, T.water],
  },
  'postpartum-recovery': {
    programs: [P.pregnancy, P.fitness],
    guides: [G.postnatal, G.pregnancy],
    tools: [T.bmi, T.protein],
  },
  'medical-nutrition-therapy': {
    programs: [P.doctorled, P.fitness],
    guides: [G.oil, G.pcos, G.proteinCompare],
    tools: [T.tdee, T.deficit, T.health],
  },
  'online-fitness-coaching': {
    programs: [P.doctorled, P.mnt],
    guides: [G.sports, G.proteinPowder],
    tools: [T.oneRepMax, T.hrZones, T.vo2],
  },
  'pcos-diet-plan': {
    programs: [P.fertility, P.mnt],
    guides: [G.pcos, G.fertility],
    tools: [T.whr, T.bmi, T.macro],
  },

  // ===================== BLOG ARTICLES =====================
  'pcos-diet-plan-complete-guide': {
    programs: [P.pmos, P.fertility],
    guides: [G.fertility],
    tools: [T.whr, T.bmi, T.macro],
  },
  'fertility-nutrition-hormone-balance': {
    programs: [P.fertility, P.pmos],
    guides: [G.pcos],
    tools: [T.whr, T.bmi],
  },
  'best-diet-plan-for-pregnancy': {
    programs: [P.pregnancy, P.postpartum],
    guides: [G.postnatal],
    tools: [T.pregnancyWeight, T.water],
  },
  'best-diet-plan-after-delivery': {
    programs: [P.postpartum, P.pregnancy],
    guides: [G.pregnancy],
    tools: [T.bmi, T.protein],
  },
  'best-cooking-oil-indian-cooking-guide': {
    programs: [P.mnt, P.doctorled],
    guides: [G.proteinPowder, G.proteinCompare],
    tools: [T.tdee, T.macro],
  },
  'types-of-protein-powder-comparison': {
    programs: [P.mnt, P.fitness],
    guides: [G.proteinCompare, G.oil],
    tools: [T.protein, T.macro],
  },
  'prawns-fish-chicken-protein-comparison': {
    programs: [P.mnt, P.fitness],
    guides: [G.proteinPowder, G.oil],
    tools: [T.protein, T.macro],
  },
  'weekend-sports-nutrition-warmup-guide': {
    programs: [P.fitness, P.doctorled],
    guides: [G.proteinCompare],
    tools: [T.hrZones, T.vo2, T.pace],
  },

  // ===================== CALCULATORS =====================
  bmi: { programs: [P.doctorled, P.mnt], guides: [G.oil], tools: [T.bodyFat, T.idealWeight, T.whr] },
  'bmi-prime': { programs: [P.doctorled], guides: [G.oil], tools: [T.bmi, T.bodyFat, T.whr] },
  'body-fat': { programs: [P.doctorled, P.fitness], guides: [G.proteinPowder], tools: [T.lbm, T.ffmi, T.bmi] },
  'lean-body-mass': { programs: [P.fitness], guides: [G.proteinCompare], tools: [T.bodyFat, T.ffmi, T.protein] },
  'ideal-weight': { programs: [P.doctorled, P.mnt], guides: [G.oil], tools: [T.bmi, T.bodyFat] },
  ffmi: { programs: [P.fitness], guides: [G.proteinPowder], tools: [T.lbm, T.bodyFat, T.protein] },
  'waist-to-hip': { programs: [P.pmos, P.mnt], guides: [G.pcos], tools: [T.bmi, T.bodyFat, T.health] },
  bsa: { programs: [P.mnt], guides: [], tools: [T.bmi, T.idealWeight] },
  tdee: { programs: [P.mnt, P.doctorled], guides: [G.oil], tools: [T.deficit, T.macro, T.protein] },
  'calorie-deficit': { programs: [P.mnt, P.doctorled], guides: [G.proteinPowder], tools: [T.tdee, T.macro, T.steps] },
  macro: { programs: [P.mnt, P.fitness], guides: [G.proteinPowder, G.proteinCompare], tools: [T.tdee, T.protein] },
  'protein-intake': { programs: [P.fitness, P.mnt], guides: [G.proteinPowder, G.proteinCompare], tools: [T.macro, T.lbm] },
  'water-intake': { programs: [P.doctorled], guides: [G.sports], tools: [T.tdee, T.calorieBurn] },
  'intermittent-fasting': { programs: [P.mnt, P.doctorled], guides: [G.oil], tools: [T.tdee, T.deficit] },
  'one-rep-max': { programs: [P.fitness], guides: [G.sports], tools: [T.ffmi, T.protein] },
  'heart-rate-zones': { programs: [P.fitness, P.doctorled], guides: [G.sports], tools: [T.vo2, T.calorieBurn] },
  'vo2-max': { programs: [P.fitness], guides: [G.sports], tools: [T.hrZones, T.pace] },
  pace: { programs: [P.fitness], guides: [G.sports], tools: [T.vo2, T.calorieBurn] },
  'calorie-burn': { programs: [P.fitness, P.doctorled], guides: [G.sports], tools: [T.steps, T.tdee] },
  'steps-to-calories': { programs: [P.doctorled, P.fitness], guides: [G.sports], tools: [T.calorieBurn, T.tdee] },
  'pregnancy-weight-gain': { programs: [P.pregnancy, P.postpartum], guides: [G.pregnancy, G.postnatal], tools: [T.bmi] },
  sleep: { programs: [P.doctorled], guides: [G.sports], tools: [T.hrZones, T.health] },
};

export default RELATED_LINKS;
